import { EditIcon, EyeIcon, WarningIcon } from "@xola/icons";
import { Badge, FadeIn, Tooltip } from "@xola/ui-kit";
import pluralize from "pluralize";
import React, { useRef } from "react";
import { useHistory } from "react-router-dom";
import { Merchandise } from "types";
import { MerchandiseActionButton } from "./MerchandiseActionButton";
import { ProductBadge } from "./ProductBadge";
import { NoTranslate } from "@/components/Common/NoTranslate";
import { ProductImage } from "@/components/Common/ProductImage";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import {
    getMerchandiseDraftReasons,
    getTotalVariantCount,
    hasVariantsEnabled,
    isMerchandiseDraft,
    isMerchandiseVisible,
} from "@/models/Merchandise";

export const MerchandiseTile = ({ merchandise }: { merchandise: Merchandise }) => {
    const excerptRef = useRef<HTMLDivElement | null>(null);
    const element = excerptRef.current?.childNodes?.[0] as HTMLElement | null;
    const isTruncated = element ? element.scrollHeight > element.clientHeight : false;

    const history = useHistory();
    const isHidden = !isMerchandiseVisible(merchandise);
    const isDraft = isMerchandiseDraft(merchandise);
    const draftReasons = getMerchandiseDraftReasons(merchandise);
    const isIncomplete = isDraft || draftReasons.length > 0;
    const variantCount = getTotalVariantCount(merchandise);
    const hasEnabledVariants = hasVariantsEnabled(merchandise);

    const handleClick = () => {
        history.push(`/products/merchandise/${merchandise.id}/descriptive`);
    };

    return (
        <div className="group flex cursor-pointer rounded p-2" data-testid="merchandise-tile" onClick={handleClick}>
            <ProductImage product={merchandise} size="huge" />
            <div className="ml-4 w-full">
                <div className="flex justify-between">
                    <NoTranslate className="text-lg font-bold line-clamp-2">{merchandise.name}</NoTranslate>
                    <div className="flex min-w-24 justify-end space-x-2">
                        <SecondaryButton
                            size="small"
                            className="invisible group-hover:visible"
                            icon={<EditIcon />}
                            data-testid="merchandise-tile-edit-button"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleClick();
                            }}
                        >
                            Edit
                        </SecondaryButton>
                        <MerchandiseActionButton merchandise={merchandise} />
                    </div>
                </div>

                <Tooltip
                    disabled={!isTruncated}
                    content={
                        <NoTranslate data-testid="merchandise-tile-tooltip-desc">{merchandise.excerpt}</NoTranslate>
                    }
                >
                    <div ref={excerptRef}>
                        <NoTranslate
                            className="pt-1 text-base text-gray-darker line-clamp-2"
                            data-testid="merchandise-tile-desc"
                        >
                            {merchandise.excerpt}
                        </NoTranslate>
                    </div>
                </Tooltip>

                <div className="mt-2 flex space-x-2">
                    {isHidden && <ProductBadge text="Hidden" color="secondary" size="medium" icon={<EyeIcon />} />}
                    {isIncomplete && (
                        <FadeIn>
                            <Tooltip
                                disabled={draftReasons.length === 0}
                                role="tooltip"
                                placement="bottom"
                                content={<ReasonList reasons={draftReasons} />}
                            >
                                <ProductBadge text="Incomplete" color="danger" size="medium" icon={<WarningIcon />} />
                            </Tooltip>
                        </FadeIn>
                    )}

                    {hasEnabledVariants && variantCount > 0 && (
                        <Badge color="secondary" size="medium">
                            {variantCount} {pluralize("Variant", variantCount)}
                        </Badge>
                    )}
                    {merchandise.inventory !== undefined && (
                        <Badge color="secondary" size="medium">
                            {merchandise.inventory} Inventory
                        </Badge>
                    )}
                </div>
            </div>
        </div>
    );
};

const ReasonList = ({ reasons }: { reasons: string[] }) => {
    return (
        <ul className="ml-3 list-disc space-y-2 p-2">
            {reasons.map((reason) => (
                <li key={reason}>{reason}</li>
            ))}
        </ul>
    );
};
